<template>
  <div
    v-if="event && !event.allDay && isCurrent"
    class="event-progress-bar"
    :class="{ withRemaining }"
  >
    <div class="bar">
      <hr class="progress" :style="progress" />
    </div>

    <p v-if="withRemaining" class="remaining-time">{{ remainingTime }} left</p>
  </div>
</template>

<script lang="ts">
import { formatDuration, intervalToDuration } from "date-fns";
import { computed, defineComponent, PropType, toRefs } from "vue";

import type { Event } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";

export default defineComponent({
  props: {
    event: {
      type: Object as PropType<Event>,
      required: false,
    },
    withRemaining: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { event } = toRefs(props);
    const clock = inject.clock();

    const progress = computed(() => {
      const progress = event.value
        ? Math.round(
            ((clock.value.getTime() - event.value.start.getTime()) /
              (event.value.end.getTime() - event.value.start.getTime())) *
              100
          )
        : 0;
      return {
        width: `${progress}%`,
      };
    });
    const isCurrent = computed<boolean>(() => {
      return event.value
        ? clock.value >= event.value.start && clock.value < event.value.end
        : false;
    });
    const remainingTime = computed(() => {
      const duration = intervalToDuration({
        start: clock.value,
        end: event.value ? event.value.end : clock.value,
      });
      return formatDuration(duration, {
        format: ["days", "hours", "minutes"],
      });
    });

    return { isCurrent, progress, remainingTime };
  },
});
</script>

<style scoped lang="scss">
.event-progress-bar {
  &.withRemaining {
    position: relative;
    display: flex;
    align-items: center;

    .bar {
      @include themed() {
        background: t($grey-15);
      }
    }
  }

  .progress {
    top: 0;
    margin: 0;
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.12);
    border: none;
    height: 2px;
    border-radius: 10px;
    position: absolute;
    width: 0%;
    max-width: 100%;
    min-width: 5%;
    @include themed() {
      background: t($secondary);
    }
  }

  .bar {
    flex: 1;
    position: relative;
    height: 2px;
    border: none;
    border-radius: 10px;
  }

  .remaining-time {
    margin-inline-start: 12px;
    @extend %font-small-medium;
    @include themed() {
      color: t($secondary);
    }
  }
}
</style>
